import {
	ChangeDetectionStrategy,
	Component,
	effect,
	input,
	output,
	computed,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JeptoPageEditorManagePagesComponent } from './jepto-page-editor-manage-pages/jepto-page-editor-manage-pages.component';
import { JeptoPageEditorTabsComponent } from './jepto-page-editor-tabs/jepto-page-editor-tabs.component';
import type { ThemeConfig } from '@framework/analytics/models/theme.model';
import type { PageEditorPage } from './page-editor.model';
import { getDefaultNextPage } from './page-editor.model';

@Component({
	selector: 'jepto-page-editor',
	standalone: true,
	imports: [
		CommonModule,
		JeptoPageEditorTabsComponent,
		JeptoPageEditorManagePagesComponent,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
	],
	templateUrl: './jepto-page-editor.component.html',
	styleUrl: './jepto-page-editor.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'jepto-page-editor-host' },
})
export class JeptoPageEditorComponent {
	/** Initial list of pages. If empty, starts with one default page. */
	readonly initialPages = input<PageEditorPage[]>([]);
	/** Optionally control the selected index from parent (0-based). */
	readonly selectedIndex = input<number | undefined>(undefined);
	/** Optionally control the selected page by id (takes precedence over selectedIndex when set). */
	readonly selectedPageId = input<string | undefined>(undefined);
	/**
	 * Theme configuration (colors, typography, card, palettes).
	 * Parent must pass this and is responsible for applying theme via ThemeService.loadThemeFromConfig.
	 */
	readonly themeConfig = input.required<ThemeConfig>();
	/**
	 * When true, only tabs are shown for navigation; the add button is hidden.
	 * Used for read-only display (e.g. analytics app viewing a report).
	 */
	readonly viewOnly = input<boolean>(false);
	/** Optional data-testid for the root. */
	readonly testId = input<string | null>(null);
	/** Aria label for the tab list. */
	readonly ariaLabel = input<string>('Pages');
	/** When true and viewport is mobile, open the page dropdown on init (e.g. for demos). */
	readonly initialDropdownOpen = input<boolean>(false);
	/** When 'transparent', content area has no background (e.g. when showing loading state). */
	readonly contentBackground = input<'default' | 'transparent'>('default');
	/**
	 * When true, suppresses tab chrome and the default placeholder page until
	 * `initialPages` is populated (e.g. while a report is loading from the API).
	 */
	readonly loading = input<boolean>(false);
	/**
	 * When true, tabs are shown in preview/view mode. Default true.
	 * For the value to persist when changed via the Edit Pages panel, the consumer must
	 * update this input when `tabsEnabledInPreviewChange` fires (e.g. two-way binding).
	 */
	readonly tabsEnabledInPreview = input<boolean>(true);

	// Internal state for pages and selected index
	protected _pages = signal<PageEditorPage[]>([]);
	protected _selectedIndex = signal<number>(0);
	/** Manage pages panel open state (edit mode only). */
	protected managePanelOpen = signal(false);
	/** Working copy of tabs enabled in preview (from parent or default). */
	protected _tabsEnabledInPreview = signal(true);

	/** Emitted when the user selects a different tab (0-based index). */
	readonly pageChange = output<number>();
	/** Emitted when the user selects a different tab (page id). */
	readonly pageChangeById = output<string>();
	/**
	 * Full page list after any mutation from the tab-bar add control or the manage-pages panel.
	 * Fires live (no batch Save); host applies immediately and owns persistence timing.
	 */
	readonly pagesChange = output<PageEditorPage[]>();
	/** Emitted when the user requests page delete from manage-pages; host confirms and persists. */
	readonly pageDeleteRequest = output<string>();
	/** Emitted when the user clicks add on the tab bar; also reflected in the next `pagesChange`. */
	readonly pageAdded = output<PageEditorPage>();
	/**
	 * Emitted when **Show Page Tabs** changes in the manage-pages panel, as soon as the toggle moves.
	 * Consumer must echo into `tabsEnabledInPreview` for the value to survive navigation.
	 */
	readonly tabsEnabledInPreviewChange = output<boolean>();
	/** Emitted when the Edit Pages slide-over opens or closes (for host overlays, e.g. hide report chat). */
	readonly managePagesPanelOpenChange = output<boolean>();

	constructor() {
		// Initialize internal pages on first render or when initialPages changes
		effect(
			() => {
				const initial = this.initialPages();
				const isLoading = this.loading();
				if (initial.length > 0) {
					this._pages.set(initial);
				} else if (isLoading) {
					this._pages.set([]);
				} else if (this._pages().length === 0) {
					// New report with no pages yet — start with one default page
					this._pages.set([getDefaultNextPage([])]);
				}
			},
			{ allowSignalWrites: true }
		);

		// Sync selected index from selectedPageId (precedence) or selectedIndex
		effect(
			() => {
				const pages = this._pages();
				const byId = this.selectedPageId();
				if (byId !== undefined && byId !== '') {
					const norm = (id: string | null | undefined): string => {
						if (id == null) return '';
						return id.startsWith('page_') ? id.substring(5) : id;
					};
					const normalized = norm(byId);
					const idx = pages.findIndex(p => norm(p.id) === normalized);
					if (idx >= 0) {
						this._selectedIndex.set(idx);
					}
					return;
				}
				const controlledIndex = this.selectedIndex();
				if (controlledIndex !== undefined) {
					const newIdx = Math.max(
						0,
						Math.min(controlledIndex, pages.length - 1)
					);
					this._selectedIndex.set(newIdx);
				} else if (this._selectedIndex() >= pages.length) {
					this._selectedIndex.set(Math.max(0, pages.length - 1));
				}
			},
			{ allowSignalWrites: true }
		);

		// Sync tabsEnabledInPreview from parent
		effect(() => {
			this._tabsEnabledInPreview.set(this.tabsEnabledInPreview());
		});

		// In view mode, if the selected page is hidden, switch to first visible page
		effect(
			() => {
				if (!this.viewOnly()) return;
				const pages = this._pages();
				const idx = this._selectedIndex();
				const page = pages[idx];
				if (page?.hidden) {
					const firstVisible = pages.findIndex(p => !p.hidden);
					if (firstVisible >= 0) {
						this._selectedIndex.set(firstVisible);
					}
				}
			},
			{ allowSignalWrites: true }
		);
	}

	onAddTab(): void {
		const newPages = this._pages();
		const nextPage = getDefaultNextPage(newPages);
		const updatedPages = [...newPages, nextPage];

		this._pages.set(updatedPages);
		this._selectedIndex.set(updatedPages.length - 1); // Select the new tab

		this.pageAdded.emit(nextPage);
		this.pagesChange.emit(updatedPages);
	}

	// Computed properties for template binding to jepto-page-editor-tabs
	readonly currentPages = computed(() => this._pages());

	readonly currentSelectedIndex = computed(() => this._selectedIndex());

	/** In view mode, only non-hidden pages; in edit mode, all pages. */
	readonly displayedPages = computed(() => {
		const pages = this._pages();
		if (this.viewOnly()) {
			return pages.filter(p => !p.hidden);
		}
		return pages;
	});

	/** Index into displayedPages for the currently selected page. -1 if selection is hidden in view mode. */
	readonly displayedSelectedIndex = computed(() => {
		const pages = this.displayedPages();
		const active = this.activePage();
		if (!active) return 0;
		const idx = pages.findIndex(p => p.id === active.id);
		return idx >= 0 ? idx : 0;
	});

	readonly activePage = computed(() => {
		const pages = this.currentPages();
		const idx = this.currentSelectedIndex();
		return pages[idx] ?? null;
	});

	/**
	 * Preview-only: themed page tabs above the content region (inherits :root ThemeService tokens).
	 * Visible whenever preview is active and tabs-in-preview is enabled, including single-page reports.
	 */
	readonly showPreviewModeTabs = computed(() => {
		if (this.loading()) return false;
		if (!this.viewOnly()) return false;
		return this._tabsEnabledInPreview();
	});

	/** Edit-only: outer `jepto-page-editor__tabs-bar` with add/manage FABs (SCSS pins light chrome tokens). */
	readonly showEditChromeTabsBar = computed(
		() => !this.viewOnly() && !this.loading()
	);

	/**
	 * Edit-only: holds the tabs bar's height while loading so the report body
	 * does not shift down when the real tabs arrive.
	 */
	readonly showEditChromeTabsSkeleton = computed(
		() => !this.viewOnly() && this.loading()
	);

	readonly tabsSkeletonWidths = [56, 72, 64];

	onTabChange(displayedIndex: number): void {
		const displayed = this.displayedPages();
		const page = displayed[displayedIndex];
		if (!page) return;
		const fullIndex = this._pages().findIndex(p => p.id === page.id);
		if (fullIndex < 0) return;
		this._selectedIndex.set(fullIndex);
		this.pageChange.emit(fullIndex);
		this.pageChangeById.emit(page.id);
	}

	openManagePanel(): void {
		if (this.managePanelOpen()) {
			return;
		}
		this.managePanelOpen.set(true);
		this.managePagesPanelOpenChange.emit(true);
	}

	closeManagePanel(): void {
		if (!this.managePanelOpen()) {
			return;
		}
		this.managePanelOpen.set(false);
		this.managePagesPanelOpenChange.emit(false);
	}

	onPageDeleteRequest(pageId: string): void {
		this.pageDeleteRequest.emit(pageId);
	}

	onManagePagesChange(updated: PageEditorPage[]): void {
		const previous = this._pages();
		const activeId = this.activePage()?.id;
		this._pages.set(updated);
		if (activeId) {
			const newIndex = updated.findIndex(p => p.id === activeId);
			if (newIndex >= 0) this._selectedIndex.set(newIndex);
		}
		const previousIds = new Set(previous.map(p => p.id));
		for (const page of updated) {
			if (!previousIds.has(page.id)) {
				this.pageAdded.emit(page);
			}
		}
		this.pagesChange.emit(updated);
	}

	onTabsEnabledInPreviewChange(enabled: boolean): void {
		this._tabsEnabledInPreview.set(enabled);
		this.tabsEnabledInPreviewChange.emit(enabled);
	}
}
